import React from 'react';
import { BodyCopy, Heading04, Heading06 } from '@uds/typography';
import Badge, { Kind as BadgeKind } from '@uds/badge';
import { useBreakpoints } from '@ux/responsive';
import { CTA } from 'core/ribbons/cta';
import { ImagesWithAspectRatios } from 'core/ribbons/primitives';
import { Colorway, Colorway as Against } from '@uds/theme-utils';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import getActiveImageWithAspectRatios from 'presentation/utils/getActiveImageWithAspectRatios';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import Card from 'components/common/Card';

import getClassNames from './AdvertCard.stylesheet';

export interface AdvertCardProps {
  imageUrl?: string;
  illustration?: string;
  imageAspectRatio?: ImageAspectRatio;
  imagesWithAspectRatios?: ImagesWithAspectRatios;
  precursor: ShortcodeProps;
  badge: string;
  title: ShortcodeProps;
  body: ShortcodeProps;
  ctas: Array<CTA>;
}

interface OuterProps extends AdvertCardProps {
  key?: string;
  colorway: Colorway;
  hBaseline?: number;
  ribbonId: string;
}

const PureAdvert = ({
  colorway,
  imageUrl,
  illustration,
  imageAspectRatio,
  imagesWithAspectRatios,
  precursor,
  badge,
  title,
  body,
  hBaseline = 1,
  ctas,
  ribbonId,
}: OuterProps) => {
  const breakpoints = useBreakpoints();
  const classNames = getClassNames();
  const hasBadge = badge && badge.length > 0;
  const hasPrecursor = !isNilOrEmpty(precursor);
  const activeImage = getActiveImageWithAspectRatios(
    breakpoints,
    {
      imageUrl,
      imageAspectRatio,
    },
    imagesWithAspectRatios,
  );

  return (
    <Card
      colorway={colorway}
      image={{
        imageUrl: activeImage.url,
        illustration,
        imageAspectRatio: activeImage.imageAspectRatio,
      }}
      ctas={ctas}
      ribbonId={ribbonId}
    >
      <>
        {hasPrecursor && (
          <Heading06 className={classNames.category} component="p">
            <>
              <Shortcode against={Against.LIGHT} code={precursor} />
              {hasBadge && <Badge kind={BadgeKind.HIGHLIGHT}>{badge}</Badge>}
            </>
          </Heading06>
        )}
        <Heading04 depth={hBaseline} className={classNames.title}>
          <Shortcode against={Against.LIGHT} code={title} />
        </Heading04>
        <div className={classNames.body}>
          <BodyCopy>
            <Shortcode against={Against.LIGHT} code={body} />
          </BodyCopy>
        </div>
      </>
    </Card>
  );
};

PureAdvert.displayName = 'Advert';

export default PureAdvert;
